// Sagas for respondent/candidate app

import { put, takeEvery, select } from 'redux-saga/effects'
import Api from 'utils/api.js'
import { startTimer } from 'ducks/timer.js'
import { errorAction } from 'ducks/error.js'
import {
  resetRespondent,
  updateRespondent,
  getRespondent,
  START_GMA,
  FINISH_GMA,
  finishGMA,
  CHANGE_GMA_ANSWER,
  START_PERSONALITY,
  FINISH_PERSONALITY,
  CHANGE_PERSONALITY_ANSWER,
  CHANGE_LANGUAGE,
} from 'ducks/respondent.js'
import {
  getLocation,
  QUIZ_MAIN,
  PERSONALITY_QUIZ,
  GMA_QUIZ,
  GMA_QUIZ_GUIDE,
  GMA_QUIZ_WELCOME,
  CANDIDATE_REPORT,
  EXTERNAL_CANDIDATE_REPORT,
  toGmaQuiz,
  toPersonalityQuiz,
} from 'ducks/router.js'

export default function* respondentSaga() {
  yield takeEvery([START_PERSONALITY, START_GMA], redirectSaga)
  yield takeEvery(
    [
      CANDIDATE_REPORT,
      EXTERNAL_CANDIDATE_REPORT,
      PERSONALITY_QUIZ,
      GMA_QUIZ,
      GMA_QUIZ_GUIDE,
      GMA_QUIZ_WELCOME,
      QUIZ_MAIN,
    ],
    fetchRespondentSaga,
  )
  yield takeEvery(CHANGE_LANGUAGE, fetchInstrumentsSaga)
  yield takeEvery(
    [
      CHANGE_LANGUAGE,
      START_PERSONALITY,
      CHANGE_PERSONALITY_ANSWER,
      START_GMA,
      CHANGE_GMA_ANSWER,
    ],
    patchRespondentSaga,
  )
  yield takeEvery(FINISH_GMA, finishGMASaga)
  yield takeEvery(FINISH_PERSONALITY, finishPersonalitySaga)
}

function* fetchInstrumentsSaga(action) {
  const { personality, language } = yield select(getRespondent)
  if (personality.language !== language) {
    const { error, response } = yield Api.get('instruments', { language })
    if (error) yield put(errorAction(error))
    else yield put(updateRespondent(reshapeInstruments(response)))
  }
}

function* fetchRespondentSaga(action) {
  const { uuid } = action.payload
  const { id } = yield select(getRespondent)

  if (id !== uuid || action.type === QUIZ_MAIN) {
    // Fetch if the id has changed or user enters main page
    // Changing id should only really happen during QA testing, since a responent
    // would only ever visit their own tests
    // Entering main page requires fetching results pdf link
    yield put(resetRespondent())
    const { error, response, meta } = yield Api.get(['respondents', uuid])
    if (error) yield put(errorAction(error))
    else {
      yield put(updateRespondent(syncronizeTime(response, meta)))
      if (response.gma.status === 'started') yield put(startTimer())
    }
  }
  yield* fetchInstrumentsSaga(action)
}

const statusChanged = (prop, state, response) => {
  const s0 = state[prop].status,
    s1 = response[prop].status
  return s1 !== s0 ? s1 : null
}

function* patchRespondentSaga(action) {
  const respondent = yield select(getRespondent)
  const { response, error, meta } = yield Api.patch(
    ['respondents', respondent.id],
    {
      action,
    },
  )
  if (error) yield put(errorAction(error))
  else {
    const pc = statusChanged('personality', respondent, response)
    const gc = statusChanged('gma', respondent, response)
    if (pc || gc) {
      yield put(updateRespondent(syncronizeTime(response, meta)))
      if (gc === 'started') yield put(startTimer())
      if (gc === 'completed') yield put(finishGMA({ reason: 'patch' }))
    }
  }
}

function* redirectSaga(action) {
  const { payload: { uuid } = {} } = yield select(getLocation)
  if (action.type === START_GMA) {
    yield put(toGmaQuiz({ uuid }))
    yield put(startTimer())
  }
  if (action.type === START_PERSONALITY) {
    yield put(toPersonalityQuiz({ uuid }))
  }
}

function* finishGMASaga(action) {
  const { gma, id } = yield select(getRespondent)
  const payload = {
    answers: gma.answers,
    action: { ...action, timeDelta: gma.timeDelta },
  }
  const { response, error, meta } = yield Api.patch(
    ['respondents', id],
    payload,
  )
  if (error) yield put(errorAction(error))
  else yield put(updateRespondent(syncronizeTime(response, meta)))
}

function* finishPersonalitySaga(action) {
  const { personality, id } = yield select(getRespondent)
  const payload = { answers: personality.answers, action }
  const { response, error } = yield Api.patch(['respondents', id], payload)
  if (error) yield put(errorAction(error))
  else yield put(updateRespondent(response))
}

const syncronizeTime = ({ gma, ...payload }, { serverTime, timeDelta }) => {
  // syncronize local time and server time
  const { started, completed, secondsTotal } = gma
  // new Date().valueOf() is epoch time in milliseconds
  const t0 = new Date(started || serverTime).valueOf()
  const t1 = new Date(completed || serverTime).valueOf()
  const secondsElapsed = Math.max(0, (t1 - t0) / 1000)
  const secondsLeft = Math.max(0, secondsTotal - secondsElapsed)
  return {
    ...payload,
    gma: {
      secondsLeft,
      secondsElapsed,
      timeDelta,
      ...gma,
    },
  }
}

const reshapeInstruments = ({ language, results: instruments }) => {
  const questions = []
  for (const instrument in instruments) {
    for (const { key } of instruments[instrument].questions) {
      questions.push({ key, instrument })
    }
  }
  return { personality: { language, instruments, questions } }
}
